import { ApolloClient } from '@apollo/client';
import { TARIFF_LIST_ITEM_QUERY } from './tariffs.gql.tsx';
import type { TariffsQuery } from '@/graphql/generated';

export type TariffListNode = NonNullable<
	NonNullable<TariffsQuery['tariffs']>['nodes']
>[number];

interface TariffListItemResponse {
	tariff?: TariffListNode | null;
}

export interface SelectTariffAfterSaveOptions {
	tariffId: string;
	client: ApolloClient<any>;
	currentTariffs: TariffListNode[];
	onDataUpdate: (updatedTariffs: TariffListNode[]) => void;
	onTariffSelect: (tariff: TariffListNode) => void;
	onOpenDetailsPanel?: () => void;
}

/**
 * Fetches the latest tariff data from the server and updates the list,
 * then selects and highlights the tariff in the grid and details panel.
 *
 * @param options Configuration options for selecting the tariff
 * @returns Promise that resolves when the operation is complete
 */
export async function selectTariffAfterSave(
	options: SelectTariffAfterSaveOptions
): Promise<void> {
	const {
		tariffId,
		client,
		currentTariffs,
		onDataUpdate,
		onTariffSelect,
		onOpenDetailsPanel
	} = options;

	try {
		// Step 1: Fetch the latest tariff data from the server
		const { data } = await client.query<TariffListItemResponse>({
			query: TARIFF_LIST_ITEM_QUERY,
			variables: { id: tariffId },
			fetchPolicy: 'network-only' // Always fetch fresh data from server
		});

		const fetchedTariff = data?.tariff;

		if (!fetchedTariff) {
			console.warn(
				`Tariff with ID ${tariffId} not found after save`
			);
			return;
		}

		// Step 2: Check if tariff exists in current list
		const existingTariffIndex = currentTariffs.findIndex(
			t => t.tariffId === tariffId
		);

		let updatedTariffs: TariffListNode[];

		if (existingTariffIndex >= 0) {
			// Tariff exists - replace with updated data
			updatedTariffs = [...currentTariffs];
			updatedTariffs[existingTariffIndex] = fetchedTariff;
		} else {
			// New tariff - append to the list
			updatedTariffs = [...currentTariffs, fetchedTariff];
		}

		// Step 3: Update the list data
		onDataUpdate(updatedTariffs);

		// Step 4: Select the tariff (both in grid and details panel)
		// Use setTimeout to ensure the grid has been updated before selection
		setTimeout(() => {
			onTariffSelect(fetchedTariff);
			if (onOpenDetailsPanel) {
				onOpenDetailsPanel();
			}
		}, 50);
	} catch (error) {
		console.error('Error selecting tariff after save:', error);
		throw error;
	}
}
